import { useState, useRef, useEffect, memo } from "react";
import { Autocomplete, TextField, createFilterOptions } from "@mui/material";

import Text from "./Text";

//Typing matches from the start of the label, like a spreadsheet
const filterOptions = createFilterOptions({matchFrom: 'start'});

const EditableSelectCell = ({value, options, placeholder, theme, error, onChange}) => {
    const [editing, setEditing] = useState(false);
    const [initialInput, setInitialInput] = useState('');
    const [focused, setFocused] = useState(false);
    const displayRef = useRef(null);
    const returnFocus = useRef(false);

    const isDark = theme.White !== "#FFFFFF";
    const bgColor = isDark ? theme.Coco : theme.Checo;
    const selected = options.find(o => o.value === value) || null;
    const label = selected ? selected.label : (value || '');

    //After choosing with the keyboard, focus goes back to the cell so Tab moves to the next one
    useEffect(() => {
        if (!editing && returnFocus.current) {
            returnFocus.current = false;
            displayRef.current?.focus();
        }
    }, [editing]);

    const startEditing = (input = '') => {
        setInitialInput(input);
        setEditing(true);
    }

    const handleKeyDown = (e) => {
        if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
            e.preventDefault();
            startEditing(e.key);
        } else if (e.key === 'Enter' || e.key === 'F2' || e.key === 'ArrowDown') {
            e.preventDefault();
            startEditing();
        }
    }

    if (editing) {
        return (
            //This component is basically a dropdown with autocomplete
            <Autocomplete
                open
                autoHighlight
                disableClearable
                size="small"
                options={options}
                value={selected}
                filterOptions={filterOptions}
                getOptionLabel={(o) => o.label}
                isOptionEqualToValue={(o, v) => o.value === v.value}
                getOptionDisabled={(o) => o.disabled}
                inputValue={initialInput}
                onInputChange={(e, input, reason) => reason !== 'reset' && setInitialInput(input)}
                onChange={(e, option) => {
                    returnFocus.current = true;
                    onChange(option.value);
                    setEditing(false);
                }}
                onClose={(e, reason) => {
                    returnFocus.current = reason !== 'blur';
                    setEditing(false);
                }}
                style={{width: '100%'}}
                slotProps={{paper: {sx: {backgroundColor: theme.White, color: theme.Black, backgroundImage: 'none'}}}}
                renderInput={(params) => (
                    <TextField {...params} autoFocus placeholder={placeholder}
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                height: '30px', padding: '0 8px !important', backgroundColor: bgColor, borderRadius: '4px',
                                "& .MuiOutlinedInput-notchedOutline": {borderColor: 'transparent'},
                                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                    boxShadow: `0 0 0 1px ${theme.Magenta}, 0 0 0 2px ${theme.Cyan}`, borderColor: 'transparent'
                                },
                            },
                            "& .MuiInputBase-input": {color: theme.Black, fontSize: 15, padding: '0 !important'},
                            "& .MuiInputBase-input::placeholder": {color: theme.Lao, opacity: 1},
                        }}
                    />
                )}
            />
        );
    }

    return (
        <div ref={displayRef} tabIndex={0} onClick={() => startEditing()} onKeyDown={handleKeyDown}
            onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
            style={{
                width: '100%', height: '30px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
                boxSizing: 'border-box', padding: '0 8px', borderRadius: '4px', cursor: 'pointer', outline: 'none',
                backgroundColor: bgColor, border: error ? `1.5px solid ${theme.Orange}` : `1px solid ${label ? theme.Coco : theme.Ey}`,
                boxShadow: focused ? `0 0 0 1px ${theme.Magenta}, 0 0 0 2px ${theme.Cyan}` : 'none'
            }}>
            <Text size={15} color={label ? theme.Black : theme.Lao} style={{whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>
                {label || placeholder}
            </Text>
            <Text size={12} color={theme.SEVENB}>▾</Text>
        </div>
    );
}

export default memo(EditableSelectCell);